class ChangeVariantDrawerContent extends HTMLElement {
    constructor() {
        super();
        this.initializeComponent();
    }

    initializeComponent() {
        this.variantOptions = this.querySelectorAll('.product-form__controls-group input[type="radio"]');
        this.handleVariantOptionChange = this.handleVariantOptionChange.bind(this);
        this.setupEventListeners();
    }

    setupEventListeners() {
        this.variantOptions.forEach(radio => {
            radio.addEventListener('change', this.handleVariantOptionChange);
        });
        this.handleVariantOptionChange();
    }

    disconnectedCallback() {
        this.variantOptions.forEach(radio => {
            radio.removeEventListener('change', this.handleVariantOptionChange);
        });
    }

    handleVariantOptionChange() {
        const selectedVariant = this.getSelectedVariant();
        if (!selectedVariant) return;

        this.resetUIState();
        this.updateUIForVariant(selectedVariant);

        // Dispatch custom event when variant changes
        document.dispatchEvent(new CustomEvent('variant:changed', {
            detail: { variant: selectedVariant }
        }));
    }

    getSelectedVariant() {
        const checkedRadios = Array.from(this.variantOptions).filter(radio => radio.checked);
        return checkedRadios.length > 0 ? checkedRadios[checkedRadios.length - 1].value : null;
    }

    resetUIState() {
        this.hideAllElements();
        this.closeAllDrawers();
    }

    hideAllElements() {
        const elements = {
            options: this.querySelectorAll('.option-item'),
            drawerTriggers: this.querySelectorAll('.drawer-trigger'),
            columnTitles: this.querySelectorAll('.option-column-title'),
            optionColumns: this.querySelectorAll('.option-column')
        };

        elements.optionColumns.forEach(column => {
            column.style.maxHeight = '0px';
            column.style.overflow = 'hidden';
        });
        elements.options.forEach(option => {
            option.classList.remove('active');
            option.style.display = 'none'
        });
        elements.drawerTriggers.forEach(trigger => trigger.style.display = 'none');
        elements.columnTitles.forEach(title => title.style.display = 'none');

    }

    closeAllDrawers() {
        const drawers = document.querySelectorAll('.variant-options-drawer');
        drawers.forEach(drawer => drawer.classList.remove('is-active'));
        document.body.classList.remove('drawer-open');
    }

    updateUIForVariant(variantValue) {
        const nestedOptions = this.getNestedOptionsForVariant(variantValue);
        const activeParentIds = this.processNestedOptions(nestedOptions);
        this.updateDrawerTriggers(activeParentIds);
    }

    getNestedOptionsForVariant(variantValue) {
        return this.querySelectorAll(`.option-item[data-variant-connection="${variantValue}"]`);
    }

    processNestedOptions(nestedOptions) {
        const activeParentIds = new Set();

        nestedOptions.forEach(option => {
            const parentId = option.getAttribute('data-parent-id');

            if (this.isDrawerChildOption(option)) {
                this.processDrawerChildOption(option, parentId, activeParentIds);
            } else {
                this.processRegularOption(option, parentId);
            }
        });

        return activeParentIds;
    }

    isDrawerChildOption(option) {
        return option.classList.contains('drawer-option-child');
    }

    processDrawerChildOption(option, parentId, activeParentIds) {
        if (!parentId) return;

        const parentElement = this.querySelector(`.option-item[data-option-id="${parentId}"]`);
        if (parentElement?.classList.contains('active')) {
            activeParentIds.add(parentId);
        }
    }

    processRegularOption(option, parentId) {
        if (!parentId) {
            const currentVariantAvailable = this.querySelector('variant-radios input:checked.disabled')
            if(!!currentVariantAvailable) return;

            option.style.display = 'flex';

            const level = option.closest('.option-column')?.getAttribute('data-level');
            if (level) {
                const columnTitle = document.querySelector(`.option-column[data-level="${level}"] .option-column-title`);
                if (columnTitle) {
                    columnTitle.style.display = 'block';
                }
                // Show the column by resetting maxHeight and overflow
                const column = option.closest('.option-column');
                if (column) {
                    column.style.maxHeight = '400px';
                }
            }
            return;
        }

        const parentElement = this.querySelector(`.option-item[data-option-id="${parentId}"]`);
        if (parentElement?.classList.contains('active')) {
            option.style.display = 'flex';
            const level = option.closest('.option-column')?.getAttribute('data-level');
            if (level) {
                const columnTitle = document.querySelector(`.option-column[data-level="${level}"] .option-column-title`);
                if (columnTitle) {
                    columnTitle.style.display = 'block';
                }
                // Show the column by resetting maxHeight and overflow
                const column = option.closest('.option-column');
                if (column) {
                    column.style.maxHeight = '400px';
                }
            }
        }
    }

    updateDrawerTriggers(activeParentIds) {
        activeParentIds.forEach(parentId => {
            const drawerTrigger = this.querySelector(`.drawer-trigger[data-parent-option="${parentId}"]`);
            const optionColumn = drawerTrigger.closest('.option-column');
            if (drawerTrigger) {
                drawerTrigger.style.display = 'flex';
                optionColumn.style.maxHeight = '400px';
            }
        });
    }
  }

  if (!customElements.get('change-variant-drawer-content')) {
      customElements.define('change-variant-drawer-content', ChangeVariantDrawerContent);
  }


  class VariantPickerStepsMetafields extends HTMLElement {
      constructor() {
          super();
          this.initializeState();
      }

      initializeState() {
          this.container = null;
          this.restructured = null;
          this.columns = {};
          this.optionCounter = 0;
      }

      connectedCallback() {
          this.initializeComponent();
          this.setupBundleProductListeners();
      }

      initializeComponent() {
          if (!this.findContainer()) return;
          this.processAllOptions();
          this.buildRestructuredDOM();
          this.setupEventListeners();

          this.updateInitialColumnTitles();
      }

      findContainer() {
          this.container = this.querySelector('.variant-picker-steps');
          if (!this.container) {
              console.warn('No .variant-picker-steps container found');
              return false;
          }
          return true;
      }

      setupBundleProductListeners() {
          // Only listen to bundle products within this variant picker instance
          const bundleProducts = this.querySelectorAll('[data-bundle-product]');
          bundleProducts.forEach(product => {
              product.addEventListener('click', () => {
                  this.setAttribute('data-bundle-products-id', product.dataset.bundleProduct);
              });
          });
      }

      processAllOptions() {
          const variantGroups = this.container.querySelectorAll('.variant-options');
          variantGroups.forEach(variantGroup => {
              const topOptions = variantGroup.querySelectorAll('.variant-option > .option-level');
              topOptions.forEach(topOptionEl => {
                  this.processOptionLevel(topOptionEl, 0, null);
              });
          });
      }

      processOptionLevel(optionEl, depth = 0, parentId = null) {
          const item = optionEl.querySelector('.option-item');
          if (!item) return;

          this.processOptionItem(item, depth, parentId);
          this.processNestedOptions(optionEl, depth, item);
      }

      processOptionItem(item, depth, parentId) {
          const uniqueId = this.generateUniqueId();
          this.setItemAttributes(item, uniqueId, depth, parentId);
          this.addItemToColumn(item, depth);
      }

      generateUniqueId() {
          return `option-${this.optionCounter++}`;
      }

      setItemAttributes(item, uniqueId, depth, parentId) {
          item.setAttribute('data-option-id', uniqueId);
          item.setAttribute('data-level', depth);

          if (parentId) {
              item.setAttribute('data-parent-id', parentId);
              this.handleDrawerClassification(item, parentId);
          }
      }

      addItemToColumn(item, depth) {
          if (!this.columns[depth]) {
              this.columns[depth] = [];
          }
          this.columns[depth].push(item.cloneNode(true));
      }

      handleDrawerClassification(item, parentId) {
          const parentItem = this.container.querySelector(`[data-option-id="${parentId}"]`);
          if (parentItem?.getAttribute('data-child-options-drawer') === 'true') {
              item.classList.add('drawer-option-child');
          }
      }

      processNestedOptions(optionEl, depth, item) {
          const nested = optionEl.querySelector('.nested-options');
          if (!nested) return;

          const nestedOptions = nested.querySelectorAll(':scope > .option-level');
          nestedOptions.forEach(nestedEl => {
              this.processOptionLevel(
                  nestedEl,
                  depth + 1,
                  item?.getAttribute('data-option-id')
              );
          });
      }

      buildRestructuredDOM() {
          this.createRestructuredContainer();
          this.createColumns();
          this.replaceOldContainer();
      }

      createRestructuredContainer() {
          this.restructured = document.createElement('div');
          this.restructured.classList.add('restructured-variant-picker');
      }

      createColumns() {
          Object.keys(this.columns)
              .map(Number)
              .sort((a, b) => a - b)
              .forEach(this.createColumn.bind(this));
      }

      createColumn(depth) {
          const column = this.createColumnElement(depth);

          // Add title container
          const titleContainer = this.createColumnTitle(depth);
          if (titleContainer) {
              column.appendChild(titleContainer);
          }

          // Create a wrapper for options
          const optionsWrapper = document.createElement('div');
          optionsWrapper.classList.add('options-wrapper');

          // Populate the wrapper instead of the column directly
          this.populateColumn(optionsWrapper, depth);

          // Add the wrapper to the column
          column.appendChild(optionsWrapper);

          this.setColumnVisibility(column, depth);
          this.restructured.appendChild(column);
      }

      createColumnElement(depth) {
          const column = document.createElement('div');
          column.classList.add('option-column');
          column.setAttribute('data-level', depth);
          return column;
      }

      createColumnTitle(depth) {
          // Find the first non-drawer option for this level to get the correct label
          const firstRegularOption = this.columns[depth]
              .find(item => item.getAttribute('data-child-options-drawer') !== 'true');

          const optionLabel = firstRegularOption?.getAttribute('data-option-label');

          if (!optionLabel) return null;

          const titleContainer = document.createElement('div');
          titleContainer.classList.add('option-column-title');
          titleContainer.textContent = optionLabel;
          return titleContainer;
      }

      populateColumn(column, depth) {
          this.columns[depth].forEach(item => {
              column.appendChild(item);
          });
      }

      setColumnVisibility(column, depth) {
          if (depth > 0) {
              column.style.display = 'none';
          }
      }

      replaceOldContainer() {
          this.container.innerHTML = '';
          this.container.appendChild(this.restructured);
      }

      setupEventListeners() {
          this.restructured.addEventListener('click', (event) => {
              const clickedItem = event.target.closest('.option-item');
              if (!clickedItem) return;

              event.stopPropagation();
              this.handleOptionClick(clickedItem);
          });
      }


      updateProductInfo(optionItem) {
        const sectionId = this.closest('[data-section]')?.dataset?.section
        const variantId = optionItem.dataset.bundleProduct
        const preorder = optionItem.dataset.bundlePreorder === "true"
        const url = optionItem.dataset.bundleProductUrl
        const available = optionItem.dataset.bundleProductAvailable
        const showBackInStock = optionItem.dataset.bundleProductShowBackInStock === "true"

        if(!url || !variantId || !sectionId){
            return;
        }

        this.updateBackInStockForm(optionItem);

        fetch(
			`${url}?variant=${variantId}&section_id=${sectionId}`,
		)
			.then((response) => response.text())
			.then((responseText) => {
				const html = new DOMParser().parseFromString(responseText, "text/html");
				const destination = document.getElementById(
					`price-${sectionId}`,
				);
				const source = html.getElementById(
					`price-${sectionId}`,
				);

                const inventoryDestinations = document.querySelectorAll(
                    `.pdp-start-inventory-level-container`,
                );
                const inventorySources = html.querySelectorAll(
                    `.pdp-start-inventory-level-container`,
                );

                if (source && destination) destination.innerHTML = source.innerHTML;

                inventoryDestinations.forEach((inventoryDestination, index) => {
                    const inventorySource = inventorySources[index];
                    if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
                });

                const variantPicker = this.querySelector('variant-radios');
                if(variantPicker){
                    variantPicker.toggleAddButton(
                        available === "false",
                        window.variantStrings.soldOut,
                        false,
                        preorder ? window.variantStrings.preorder : null
                    );

                 }
			});
      }

      handleOptionClick(clickedItem) {
          const level = parseInt(clickedItem.getAttribute('data-level'), 10);
          const optionId = clickedItem.getAttribute('data-option-id');

          this.updateActiveState(clickedItem, level);

          if (this.isDrawerOption(clickedItem)) {
              this.handleDrawerOption(clickedItem, level, optionId);
              // Automatically trigger the drawer open
              const nextColumn = this.getNextColumn(level);
              if (nextColumn) {
                    nextColumn.querySelectorAll('.drawer-trigger').forEach(trigger => {
                        const triggerOption = trigger.dataset.parentOption
                        trigger.style.display = triggerOption !== optionId ? 'none' : 'flex';
                        if(triggerOption === optionId){
                            trigger.click();
                        }
                    });
              }
              this.updateProductInfo(clickedItem);
              this.updateBackInStockForm(clickedItem);
          } else {
              this.handleRegularOption(level, optionId);
              this.updateProductInfo(clickedItem);
              // Update preorder attribute after regular option selection
              this.updatePreorderAttribute();
          }
      }

      isDrawerOption(item) {
          return item.getAttribute('data-child-options-drawer') === 'true';
      }

      updateActiveState(clickedItem, level) {
          const currentColumn = this.restructured.querySelector(`.option-column[data-level="${level}"]`);
          if (!currentColumn) return;

          // Find all option items in the current column and remove active class
          const optionsWrapper = currentColumn.querySelector('.options-wrapper');
          if (optionsWrapper) {
              optionsWrapper.querySelectorAll('.option-item').forEach(item => {
                  item.classList.remove('active');
              });
          }

         // Remove previous active options in the drawer
          const drawerActiveOptions = document.querySelectorAll(`.variant-options-drawer[data-parent-option="${clickedItem.dataset.optionId}"] .drawer-option-child.active`);
          drawerActiveOptions.length && drawerActiveOptions.forEach(option => option.classList.remove('active'));

          clickedItem.classList.add('active');
          
          // Also update preorder attribute based on final selected option
          this.updatePreorderAttribute();
      }
      
      getLastSelectedOption() {
          const columns = Array.from(this.restructured.querySelectorAll('.option-column'));
          if (!columns.length) return null;

          return columns.reduce((lastSelected, column) => {
              if (window.getComputedStyle(column).display !== 'none') {
                  const activeOption = column.querySelector('.option-item.active');
                  return activeOption || lastSelected;
              }
              return lastSelected;
          }, null);
      }
      
      updatePreorderAttribute() {
          const productSection = this.closest('[data-section]');
          if (!productSection) return;
          
          const initialBundleButton = productSection.querySelector('bundle-add-to-cart-button');
          if (!initialBundleButton) return;
          
          const isMainProductPreorder = initialBundleButton.getAttribute('data-is-preorder') === 'true';
          
          // Only update buttons within the current product context
          productSection.querySelectorAll('bundle-add-to-cart-button').forEach(btn => {
              if (isMainProductPreorder) {
                  btn.setAttribute('data-is-preorder', 'true');
              } else {
                  btn.removeAttribute('data-is-preorder');
              }
          });
      }

      updateBackInStockForm(clickedItem) {
        // Only update forms within the current product context
        const productSection = this.closest('.product');
        if (!productSection) return;
        
        const backInStockForms = productSection.querySelectorAll('back-in-stock-form');
        const showBackInStock = clickedItem.dataset.bundleProductShowBackInStock === "true"
        const variantId = clickedItem.dataset.bundleProduct;
        backInStockForms.forEach(form => {
            form.style.display = showBackInStock ? 'block' : 'none';
            if(variantId){
                form.querySelector('input[name="variant_id"]').value = variantId;
            }
        });
      }

      handleDrawerOption(clickedItem, level, optionId) {
          const nextColumn = this.getNextColumn(level);
          if (!nextColumn) return;

          nextColumn.style.display = 'flex';
          this.setupDrawer(nextColumn, optionId);
          this.hideChildOptions(nextColumn);
      }

      getNextColumn(level) {
          return this.restructured.querySelector(`.option-column[data-level="${level + 1}"]`);
      }

      handleRegularOption(level, optionId) {
          const nextColumn = this.getNextColumn(level);
          if (!nextColumn) return;

          // Reset the column state first
          this.resetColumnState(nextColumn);


          // Hide all drawer triggers in the next column first
          const allDrawerTriggers = nextColumn.querySelectorAll('.drawer-trigger');
          allDrawerTriggers.forEach(trigger => {
              trigger.style.display = 'none';
              trigger.classList.remove('drawer-trigger--visible');
          });

          // Show all child options that belong to the selected parent
          const optionsWrapper = nextColumn.querySelector('.options-wrapper');
          if (optionsWrapper) {
              // Find the first non-drawer child option to get the correct title
              const firstRegularChild = optionsWrapper.querySelector(
                  `.option-item[data-parent-id="${optionId}"]:not([data-child-options-drawer="true"])`
              );

              // Update column title based on the regular child option
              const columnTitle = nextColumn.querySelector('.option-column-title');
              if (columnTitle && firstRegularChild) {
                  const label = firstRegularChild.getAttribute('data-option-label');
                  if (label) {
                      columnTitle.textContent = label;
                      columnTitle.style.display = 'block';
                  }
              }

              // Show all child options and their associated drawer triggers
              const childOptions = optionsWrapper.querySelectorAll(`.option-item[data-parent-id="${optionId}"]`);
              childOptions.forEach(child => {
                  child.style.display = 'flex';

                  // If this is a drawer option, show its trigger
                  if (child.getAttribute('data-child-options-drawer') === 'true') {
                      const childId = child.getAttribute('data-option-id');
                      const drawerTrigger = nextColumn.querySelector(`.drawer-trigger[data-parent-option="${childId}"]`);
                      if (drawerTrigger) {
                        // Show the next column itself
                        nextColumn.style.display = 'flex';
                        nextColumn.style.maxHeight = '400px';
                        nextColumn.style.overflow = 'visible';
                        drawerTrigger.removeAttribute('style');
                        drawerTrigger.classList.add('drawer-trigger--visible');
                    }
                  }
              });
          }

          this.hideDeeperColumns(level + 1);
      }

      resetColumnState(column) {
          // Hide all options first
          const options = column.querySelectorAll('.option-item');
          options.forEach(option => option.style.display = 'none');

          // Reset drawer triggers
          const triggers = column.querySelectorAll('.drawer-trigger');
          triggers.forEach(trigger => {
              trigger.removeAttribute('style');
              trigger.classList.remove('drawer-trigger--visible');
          });

          // Reset column title
          const columnTitle = column.querySelector('.option-column-title');
          if (columnTitle) {
              columnTitle.style.display = 'none';
          }


          // Remove active states
          options.forEach(option => option.classList.remove('active'));
      }

      hideDeeperColumns(startLevel) {
          let currentLevel = startLevel + 1;
          while (true) {
              const deeperColumn = this.restructured.querySelector(`.option-column[data-level="${currentLevel}"]`);
              if (!deeperColumn) break;

              this.resetColumnState(deeperColumn);
              deeperColumn.style.display = 'none';
              currentLevel++;
          }
      }

      hideChildOptions(nextColumn) {
          // Reset the column state
          this.resetColumnState(nextColumn);
      }

      updateInitialColumnTitles() {
          // Get the first column
          const firstColumn = this.restructured.querySelector('.option-column[data-level="0"]');
          if (firstColumn) {
              // Show the first column title
              const firstColumnTitle = firstColumn.querySelector('.option-column-title');
              if (firstColumnTitle) {
                  firstColumnTitle.style.display = 'block';
              }

              // Hide all other column titles
              const otherColumnTitles = this.restructured.querySelectorAll('.option-column:not([data-level="0"]) .option-column-title');
              otherColumnTitles.forEach(title => {
                  title.style.display = 'none';
              });
          }

          // Trigger the variant change handler to update UI based on selected variant
          const changeVariantDrawer = document.querySelector('change-variant-drawer-content');
          if (changeVariantDrawer) {
              changeVariantDrawer.handleVariantOptionChange();
          }
      }

      showErrorMessage(message) {
          // Create banner container
          const banner = document.createElement('div');
          banner.classList.add('banner', 'banner--error');

          // Create banner content
          const bannerContent = document.createElement('div');
          bannerContent.classList.add('banner__content');

          // Add message
          const messageText = document.createElement('p');
          messageText.classList.add('banner__message');
          messageText.textContent = message;

          // Add close button
          const closeButton = document.createElement('button');
          closeButton.classList.add('banner__close');
          closeButton.setAttribute('aria-label', 'Close');
          closeButton.innerHTML = '×';
          closeButton.onclick = () => banner.remove();

          // Assemble banner
          bannerContent.appendChild(messageText);
          bannerContent.appendChild(closeButton);
          banner.appendChild(bannerContent);

          // Add to document
          document.body.appendChild(banner);

          // Auto-remove after 5 seconds
          setTimeout(() => {
              if (banner.parentNode) {
                  banner?.remove();
              }
          }, 5000);
      }

      setupDrawer(nextColumn, optionId) {
          // Reset column state first
          this.resetColumnState(nextColumn);

          // Look for a specific trigger for this option
          let trigger = nextColumn.querySelector(`.drawer-trigger[data-parent-option="${optionId}"]`);

          if (!trigger) {
              trigger = this.createDrawerTrigger(nextColumn, optionId);
          } else {
              // Remove any inline display style
              trigger.removeAttribute('style');
          }

          // Use classList to control visibility instead of inline styles
          trigger.classList.add('drawer-trigger--visible');

          nextColumn.style.maxHeight = '400px';
          nextColumn.style.overflow = 'visible';
      }

      createDrawerTrigger(nextColumn, optionId) {
          const trigger = document.createElement('button');
          trigger.classList.add('drawer-trigger');
          trigger.textContent = 'Change Selection';
          trigger.setAttribute('aria-controls', `VariantOptionsDrawer-${optionId}`);
          trigger.setAttribute('data-parent-option', optionId);
          nextColumn.insertAdjacentElement('afterbegin', trigger);

          this.initializeDrawer(optionId);
          this.setupDrawerEvents(trigger, nextColumn, optionId);

          return trigger;
      }

      handleSubmitButtonClick(drawerId) {
          const drawer = document.getElementById(drawerId);
          this.closeDrawer(drawer);
      }

      initializeDrawer(optionId) {
          const drawerId = `VariantOptionsDrawer-${optionId}`;
          if (!document.getElementById(drawerId)) {
              // Create overlay if it doesn't exist
              if (!document.querySelector('.drawer-overlay')) {
                  const overlay = document.createElement('div');
                  overlay.classList.add('drawer-overlay');
                  overlay.addEventListener('click', () => {
                      const activeDrawer = document.querySelector('.variant-options-drawer.is-active');
                      if (activeDrawer) {
                          this.closeDrawer(activeDrawer);
                      }
                  });
                  document.body.appendChild(overlay);
              }

              const drawer = document.createElement('div');
              drawer.id = drawerId;
              drawer.classList.add('variant-options-drawer');
              drawer.setAttribute('data-parent-option', optionId);

              // Force initial position
              drawer.style.transition = 'none';
              drawer.style.right = '-100%';

              // Create inner container
              const innerContainer = document.createElement('div');
              innerContainer.classList.add('inner-container-drawer');

              // Create header container
              const headerContainer = document.createElement('div');
              headerContainer.classList.add('header-drawer-option');

              // Add model text to header with radio value
              const modelText = document.createElement('div');
              const parentOption = this.restructured.querySelector(`.option-item[data-option-id="${optionId}"]`);
              // Scope radio lookup to current product context
              const productContainer = this.closest('.product');
              const selectedRadio = productContainer ? productContainer.querySelector('.product-form__controls-group input[type="radio"]:checked') : null;
              const radioValue = selectedRadio ? selectedRadio.value : '';
              const parentText = parentOption ? parentOption.textContent.trim() : '';

              modelText.textContent = radioValue ? `${radioValue} (${parentText})` : parentText;

              // Create close button with bound event handler
              const closeButton = document.createElement('button');
              closeButton.classList.add('variant-options-drawer__close');
              closeButton.innerHTML = '×';
              closeButton.addEventListener('click', () => this.closeDrawer(drawer));

              // Add model text and close button to header
              headerContainer.appendChild(modelText);
              headerContainer.appendChild(closeButton);

              // Create content container
              const contentContainer = document.createElement('div');
              contentContainer.classList.add('content-drawer');

              // Add title for drawer options
              const drawerOptionsTitle = document.createElement('div');
              drawerOptionsTitle.classList.add('option-column-title');
              // Get the option label from the next column
              const nextColumn = this.restructured.querySelector(`.option-column[data-level="${parseInt(parentOption?.getAttribute('data-level'), 10) + 1}"]`);
              if (nextColumn) {
                  const titleText = nextColumn.querySelector('.option-column-title')?.textContent;
                  if (titleText) {
                      drawerOptionsTitle.textContent = titleText;
                  }
              }
              contentContainer.appendChild(drawerOptionsTitle);

              // Create options container
              const drawerOptions = document.createElement('div');
              drawerOptions.classList.add('variant-options-drawer__options');

              // Add Submit and continue button
              const submitButton = document.createElement('button');
              submitButton.classList.add('drawer-submit-button');
              submitButton.textContent = 'Submit and continue';

              // Add click event listener to close drawer
              submitButton.addEventListener('click', () => {
                  const drawer = document.getElementById(drawerId);
                  this.closeDrawer(drawer);
              });

              const drawerContentArea = document.createElement('div');
              drawerContentArea.classList.add('variant-options-drawer__content-area');

              // Structure the elements
              contentContainer.appendChild(drawerOptions);
              contentContainer.appendChild(submitButton); // Add the submit button after options
              contentContainer.appendChild(drawerContentArea);

              innerContainer.appendChild(headerContainer); // Add header first
              innerContainer.appendChild(contentContainer); // Then content
              drawer.appendChild(innerContainer);
              document.body.appendChild(drawer);

              // Force a reflow to ensure the initial position is applied
              drawer.offsetHeight;

              // Reset transition and remove inline style
              requestAnimationFrame(() => {
                  drawer.style.transition = '';
                  drawer.style.right = '';
              });
          }
      }

      setupDrawerEvents(trigger, nextColumn, optionId) {
          trigger.addEventListener('click', () => {
              const drawerId = `VariantOptionsDrawer-${optionId}`;
              const drawer = document.getElementById(drawerId);

              const allDrawers = document.querySelectorAll('.variant-options-drawer');
              allDrawers.forEach(d => {
                  if (d.id !== drawerId) {
                      d.classList.remove('is-active');
                  }
              });

              if (drawer.classList.contains('is-active')) {
                  this.closeDrawer(drawer);
                  return;
              }

              const drawerOptions = drawer.querySelector('.variant-options-drawer__options');

              if (!drawerOptions.hasChildNodes()) {
                  const drawerContentArea = drawer.querySelector('.variant-options-drawer__content-area');
                  const childItems = nextColumn.querySelectorAll(`.option-item[data-parent-id="${optionId}"]`);

                  childItems.forEach(child => {
                      const clone = child.cloneNode(true);
                      const drawerContentEl = clone.querySelector('.drawer-content-option');
                      if (drawerContentEl) {
                          drawerContentEl.style.display = 'none';
                      }
                      if (child.classList.contains('active')) {
                          clone.classList.add('active');
                      }
                      clone.style.display = 'flex';
                      drawerOptions.appendChild(clone);
                  });

                  const contentContainer = document.createElement('div');
                  contentContainer.classList.add('drawer-content-container');

                  childItems.forEach(child => {
                      const originalContent = child.querySelector('.drawer-content-option');
                      if (originalContent) {
                          const wrapper = document.createElement('div');
                          wrapper.classList.add('drawer-content-wrapper');
                          wrapper.setAttribute('data-content-for', child.getAttribute('data-option-id'));
                          wrapper.innerHTML = originalContent.innerHTML;
                          wrapper.style.display = 'none';
                          contentContainer.appendChild(wrapper);
                      }
                  });

                  drawerContentArea.appendChild(contentContainer);

                  drawerOptions.onclick = (event) => {
                      const clickedItem = event.target.closest('.option-item');
                      if (!clickedItem) return;

                      const itemId = clickedItem.getAttribute('data-option-id');


                      const allContent = drawer.querySelectorAll('.drawer-content-wrapper');
                      allContent.forEach(content => {
                          content.style.display = 'none';
                      });

                      const targetContent = drawer.querySelector(`.drawer-content-wrapper[data-content-for="${itemId}"]`);
                      if (targetContent) {
                          targetContent.style.display = 'block';
                      }

                      drawerOptions.querySelectorAll('.option-item').forEach(item => item.classList.remove('active'));
                      clickedItem.classList.add('active');

                      const mainItem = this.restructured.querySelector(`.option-item[data-option-id="${itemId}"]`);
                      if (mainItem) {
                          const level = parseInt(mainItem.getAttribute('data-level'), 10);
                          this.updateActiveState(mainItem, level);
                          this.handleRegularOption(level, itemId);
                      }
                      
                      // Update preorder attribute based on final selected subvariant
                      this.updatePreorderAttribute();

                      document.dispatchEvent(new CustomEvent('variant:drawer:click', {detail: { optionElement: clickedItem }}))
                  };
              }

              this.openDrawer(drawer);

              const submitButton = drawer.querySelector('.drawer-submit-button');
              const newSubmitButton = submitButton.cloneNode(true);
              submitButton.parentNode.replaceChild(newSubmitButton, submitButton);

              newSubmitButton.addEventListener('click', () => {
                  this.closeDrawer(drawer);
              });
          });
      }

      openDrawer(drawer) {
          drawer.classList.add('is-active');
          document.body.classList.add('drawer-open');
          // Show overlay
          const overlay = document.querySelector('.drawer-overlay');
          overlay.classList.add('is-active');
      }

      closeDrawer(drawer = null) {
          if (drawer) {
              // Always close the drawer without checking for variant selection
              drawer.classList.remove('is-active');
          } else {
              // If no specific drawer provided, close all drawers
              const allDrawers = document.querySelectorAll('.variant-options-drawer');
              allDrawers.forEach(d => {
                  d.classList.remove('is-active');
              });
          }

          // Only remove body class and hide overlay if no drawers are active
          const activeDrawers = document.querySelectorAll('.variant-options-drawer.is-active');
          if (activeDrawers.length === 0) {
              document.body.classList.remove('drawer-open');
              // Hide overlay
              const overlay = document.querySelector('.drawer-overlay');
              if (overlay) {
                  overlay.classList.remove('is-active');
              }
          }
      }
  }

  if (!window.customElements.get('variant-picker-steps-metafields')) {
      customElements.define('variant-picker-steps-metafields', VariantPickerStepsMetafields);
  }




  class BundleAddToCartButton extends HTMLElement {
      constructor() {
          super();
          this.button = this.querySelector('button');
          this.buttonText = this.button?.querySelector('.add-bundle-product__text');
          this.cartDrawer = document.querySelector('cart-drawer');
          this.quickAddModal = this.closest('quick-add-modal');
      }

      connectedCallback() {
          // For preorders, don't attach click handler to main button
          // The popup.js will intercept the click and open the popup
          // The popup button will then trigger add to cart via the global handler
          if (this.dataset.isPreorder !== 'true') {
              this.button.addEventListener('click', () => this.handleClick());
          } else {
              // Set up handler for popup button if this is a preorder button
              this.setupPopupButtonHandler();
          }
      }

      disconnectedCallback() {
          if (this.popupElement && this.boundPopupHandler) {
              this.popupElement.removeEventListener('click', this.boundPopupHandler);
          }

          this.popupElement.removeEventListener('click', this.handlePopupButtonClick);
      }
      
      setupPopupButtonHandler() {
          this.popupElement = document.querySelector('#preorderPopup');
          if (!this.popupElement) return;

          this.popupElement.addEventListener('click', this.handlePopupButtonClick);
      }

      handlePopupButtonClick = (e) => {
          const popupButton = e.target.closest('button[action-add-to-cart]');
          if (!popupButton) return;

          e.preventDefault();
          e.stopPropagation();

          if (popupButton.disabled) return;

          const bundleProductId = this.getBundleProductId();
          if (!bundleProductId) {
              console.error('Please make a selection before adding to cart');
              return;
          }

          popupButton.disabled = true;

          this.handleClick()
              .then(() => {
                  popupButton.disabled = false;
              })
              .catch((error) => {
                  console.error('Error adding bundle to cart:', error);
                  popupButton.disabled = false;
              });
      }

      async handleClick() {
          try {
              const bundleProductId = this.getBundleProductId();
              if (!bundleProductId) {
                  throw new Error('Please make a selection before adding to cart');
              }

              const quantity = this.getQuantity();

              // Set loading state at the start
              this.setLoadingState(true);

              await this.addToCart(bundleProductId, quantity);
              await this.updateCartDrawer();

              // Clear loading state on success
              this.setLoadingState(false);

              // Dispatch custom event to close popup after successful add to cart
              document.dispatchEvent(new CustomEvent('cart:item-added'));

          } catch (error) {
              this.handleError(error);
          } finally {
              // Ensure loading state is cleared even if there's an error
              this.setLoadingState(false);
          }
      }

      getBundleProductId() {
          const container = this.quickAddModal || document;
          const productContainer = this.closest('.product');
          if (!productContainer) return null;

          const variantIdInput = productContainer.querySelector('input[name="id"]');
          if (!variantIdInput) return null;

          const selectedRadio = container.querySelector('.product-form__controls-group input[type="radio"]:checked');
          if (!selectedRadio) return variantIdInput.value;

          const variantConnection = selectedRadio.value;
          const hasNestedOptions = container.querySelector(
              `.option-item[data-variant-connection="${variantConnection}"]`
          );

          // If there are no nested options, return the variant ID from the input
          if (!hasNestedOptions) {
              return variantIdInput.value;
          }

          // Only look for bundle product ID if there are nested options
          return this.getBundleProductIdFromNestedOptions();
      }

      getBundleProductIdFromNestedOptions() {
          const lastSelectedOption = this.getLastSelectedOption();
          return lastSelectedOption ? lastSelectedOption.getAttribute('data-bundle-product') : null;
      }

      getLastSelectedOption() {
          // Scope to current product container (quick-add or main PDP)
          const container = this.quickAddModal || this.closest('.product') || document;
          const variantPicker = container.querySelector('variant-picker-steps-metafields');
          if (!variantPicker) return null;

          const columns = Array.from(variantPicker.querySelectorAll('.option-column'));
          if (!columns.length) return null;

          return columns.reduce((lastSelected, column) => {
              if (this.isColumnVisible(column)) {
                  const activeOption = column.querySelector('.option-item.active');
                  return activeOption || lastSelected;
              }
              return lastSelected;
          }, null);
      }

      isColumnVisible(column) {
          return window.getComputedStyle(column).display !== 'none';
      }

      getQuantity() {
        // First try within product-form, then within the product container, fallback to 1
        let quantityInput = this.closest('product-form')?.querySelector('.quantity__input');
        
        if (!quantityInput) {
          const container = this.quickAddModal || this.closest('.product');
          quantityInput = container?.querySelector('.quantity__input');
        }
        
        return quantityInput ? parseInt(quantityInput.value, 10) : 1;
      }

      async addToCart(bundleProductId, quantity) {
          // Prepare properties object
          let properties = {};
          if (this.getLastSelectedOption() && this.getLastSelectedOption().getAttribute('data-model-name')) {
            const lastSelectedOption = this.getLastSelectedOption();
            const modelNameKeyValue = lastSelectedOption?.getAttribute('data-model-name');
            const key = modelNameKeyValue.split(':')[0];
            const value = modelNameKeyValue.split(':')[1];
            properties[key] = value;
          }

          // Check for pre-order text property (scope to current product)
          const container = this.quickAddModal || this.closest('.product') || document;
          const preorderInput = container.querySelector('input[name="properties[Pre-order]"]');
          if (preorderInput && preorderInput.value.trim() !== '') {
            properties['Pre-order'] = preorderInput.value;
          }

          // Remove the setLoadingState from here since it's handled in handleClick
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  items: [{
                      id: parseInt(bundleProductId, 10),
                      quantity: quantity,
                      properties: properties
                  }]
              })
          });

          const data = await response.json();

          if (!response.ok) {
              this.showErrorMessage(data.message);
              throw new Error(data.description || 'Error adding item to cart');
          }

          return data;
      }

      async updateCartDrawer() {
          if (!this.cartDrawer) return;

          const response = await fetch(
              `${window.Shopify.routes.root}cart?sections=cart-drawer,cart-icon-bubble`
          );
          const sectionsData = await response.json();

          this.cartDrawer.renderContents({ sections: sectionsData });
      }

      handleError(error) {
          console.error('Add to cart error:', error);
          this.showErrorMessage(error.message);
          // Remove setLoadingState from here since it's handled in the finally block
      }

      showErrorMessage(message) {
          // Create banner container
          const banner = document.createElement('div');
          banner.classList.add('banner', 'banner--error');

          // Create banner content
          const bannerContent = document.createElement('div');
          bannerContent.classList.add('banner__content');

          // Add message
          const messageText = document.createElement('p');
          messageText.classList.add('banner__message');
          messageText.textContent = message;

          // Add close button
          const closeButton = document.createElement('button');
          closeButton.classList.add('banner__close');
          closeButton.setAttribute('aria-label', 'Close');
          closeButton.innerHTML = '×';
          closeButton.onclick = () => banner.remove();

          // Assemble banner
          bannerContent.appendChild(messageText);
          bannerContent.appendChild(closeButton);
          banner.appendChild(bannerContent);

          // Add to document
          document.querySelector('.bundle-pdp-section')?.appendChild(banner);

          // Auto-remove after 5 seconds
          setTimeout(() => {
              if (banner.parentNode) {
                  banner.remove();
              }
          }, 5000);
      }

      setLoadingState(isLoading) {
          this.button.disabled = isLoading;
      }
  }

  if (!customElements.get('bundle-add-to-cart-button')) {
      customElements.define('bundle-add-to-cart-button', BundleAddToCartButton);
  }
